// AtriumBoardHome.tsx — Atrium Board Member home, "b-home" (Sprint 84, ADR-082).
// ----------------------------------------------------------------------------
// The Salesforce-style board governance home, composed from the Sprint 83
// framework and fed REAL scoped data from condoStore (board meetings,
// resolutions, fees, units, work orders, documents).
//
// HONESTY CONTRACT (ADR-082): chips/cards backed by real store data render real
// values; the prototype's "Reserve funding %" and "Operating budget % used"
// have NO backing field in the current model, so they are OMITTED. "Awaiting
// approval" maps to OPEN board resolutions (real) with an honest empty state.
// The budget-vs-actual chart has no budget figures in the store, so the
// performance card shows a real collected-revenue series and is labelled as
// such (not a fabricated budget line). Tasks-equivalent docs are real.
import React, { useEffect, useMemo } from 'react';
import { Skeleton, Button } from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  CheckOutlined,
  TeamOutlined,
  WarningOutlined,
  DollarOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import { useAuthStore } from '../../../store/authStore';
import { useCondoStore } from '../../../store/condoStore';
import type { BoardResolution } from '../../../types';
import { isScheduledStatus } from '../../../pages/board/boardTypes';

import { SFGreeting, type SFKpiChip } from './SFGreeting';
import { AtriumAreaChart } from './AreaChart';
import { RailCard } from './RailCard';
import { Assistant, type AssistantItem } from './Assistant';
import { EventsCard, type EventItem } from './EventsCard';
import { formatDate } from '../../../utils/date';
import './homeStyles.css';
import { currency as currencyFmt } from '../../../utils/currency';

// Portfolio/KPI sums read cleaner without cents; delegate to the one CAD helper.
const currency = (n: number) => currencyFmt(n, { cents: false });

export const AtriumBoardHome: React.FC = () => {
  const { user, currentCondo } = useAuthStore();
  const {
    fetchBoardMeetings,
    fetchBoardResolutions,
    fetchFees,
    fetchPayments,
    fetchUnits,
    fetchWorkOrders,
    fetchDocuments,
    boardMeetings,
    boardResolutions,
    workOrders,
    payments,
    documents,
    getOverdueFees,
    getUnitStats,
    error,
    loadingByKey,
    errorByKey,
  } = useCondoStore();

  // Sprint 106 (106.2) — gate the financial KPI chips + stat row on the home's
  // first relevant keyed fetch (fees/payments/units) so the board never sees a
  // confident $0 delinquency / 0% occupancy before the data lands.
  const RELEVANT_KEYS = ['fees', 'payments', 'units'] as const;
  const summaryLoading =
    RELEVANT_KEYS.some(k => loadingByKey[k] === true) ||
    RELEVANT_KEYS.every(k => loadingByKey[k] === undefined);
  const summaryError = RELEVANT_KEYS.map(k => errorByKey[k]).find(e => e != null) ?? null;
  const retrySummary = () => {
    if (!currentCondo?.id) return;
    fetchFees(currentCondo.id);
    fetchPayments(currentCondo.id);
    fetchUnits(currentCondo.id);
  };

  // Reuse EXISTING scoped store actions only.
  useEffect(() => {
    if (currentCondo?.id) {
      fetchBoardMeetings(currentCondo.id);
      fetchBoardResolutions(currentCondo.id);
      fetchFees(currentCondo.id);
      fetchPayments(currentCondo.id);
      fetchUnits(currentCondo.id);
      fetchWorkOrders(currentCondo.id);
      fetchDocuments(currentCondo.id);
    }
  }, [
    currentCondo?.id, fetchBoardMeetings, fetchBoardResolutions, fetchFees,
    fetchPayments, fetchUnits, fetchWorkOrders, fetchDocuments,
  ]);

  const condoId = currentCondo?.id;

  // REAL — open resolutions = "needs your vote" / awaiting board approval.
  const openResolutions = useMemo<BoardResolution[]>(
    () => boardResolutions.filter(r => r.condoId === condoId && r.status === 'open'),
    [boardResolutions, condoId],
  );

  // REAL — upcoming scheduled board meetings. R11-7: "upcoming" must exclude
  // past meetings — a scheduled-but-elapsed meeting (e.g. June's AGM) was
  // still counted here, disagreeing with the Board Portal's KPI.
  const upcomingMeetings = useMemo(
    () => {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      return boardMeetings
        .filter(m => m.condoId === condoId && isScheduledStatus(m.status)
          && new Date(m.date).getTime() >= startOfToday.getTime())
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    },
    [boardMeetings, condoId],
  );

  // REAL — delinquency (overdue fees total) + occupancy + open work orders.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- payments is intentional: getOverdueFees reads the live payments array.
  const overdueFees = useMemo(() => getOverdueFees(condoId), [getOverdueFees, condoId, payments]);
  const delinquencyTotal = overdueFees.reduce((s, f) => s + f.amount, 0);
  const unitStats = useMemo(() => getUnitStats(condoId), [getUnitStats, condoId]);
  const openWorkOrders = useMemo(
    () => workOrders.filter(w => w.condoId === condoId && w.status !== 'COMPLETED' && w.status !== 'CANCELLED'),
    [workOrders, condoId],
  );

  // REAL — board/financial documents to review.
  const reviewDocs = useMemo(
    () => documents
      .filter(d => d.condoId === condoId && (d.category === 'financial' || d.category === 'meeting-minutes'))
      .slice(0, 4),
    [documents, condoId],
  );

  // REAL chart series: revenue collected per month (last 6 months). The store
  // has no per-month BUDGET figures, so we plot ACTUAL collections and label
  // the card honestly rather than fabricate a budget-vs-actual line.
  const { chartPoints, chartLabels, hasChartData, totalCollected } = useMemo(() => {
    const now = new Date();
    const buckets: { label: string; total: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ label: d.toLocaleString('en-CA', { month: 'short' }), total: 0 });
    }
    let total = 0;
    payments
      .filter(p => p.condoId === condoId && p.status === 'completed')
      .forEach(p => {
        const d = new Date(p.paymentDate);
        const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
        if (monthsAgo >= 0 && monthsAgo <= 5) { buckets[5 - monthsAgo].total += p.amount; total += p.amount; }
      });
    return {
      chartPoints: buckets.map(b => b.total),
      chartLabels: buckets.map(b => b.label),
      hasChartData: buckets.some(b => b.total > 0),
      totalCollected: total,
    };
  }, [payments, condoId]);

  // ── KPI chips — REAL only. "Operating budget % used" and "Reserve funding %"
  // have no backing field, so they are OMITTED rather than fabricated. ──
  // 106.2 — money/occupancy chips show "—" until fees/payments/units settle.
  const chips: SFKpiChip[] = [
    { l: 'Needs your vote', v: String(openResolutions.length) },
    { l: 'Delinquency', v: (summaryLoading || summaryError) ? '—' : currency(delinquencyTotal) },
    { l: 'Occupancy', v: (summaryLoading || summaryError) ? '—' : `${unitStats.occupancyRate}%` },
    { l: 'Open work orders', v: String(openWorkOrders.length) },
  ];

  // Assistant — REAL signals.
  const assistantItems: AssistantItem[] = [];
  if (openResolutions.length > 0) {
    assistantItems.push({
      tone: 'red',
      h: `${openResolutions.length} item(s) need your vote`,
      s: 'Open board resolutions awaiting a decision',
      action: 'Vote',
    });
  }
  if (upcomingMeetings.length > 0) {
    assistantItems.push({
      tone: 'blue',
      h: `Next meeting: ${upcomingMeetings[0].title}`,
      s: `${formatDate(upcomingMeetings[0].date)} · ${upcomingMeetings[0].location}`,
      action: 'View',
    });
  }
  if (overdueFees.length > 0) {
    assistantItems.push({
      tone: 'amber',
      h: `Delinquency at ${currency(delinquencyTotal)}`,
      s: `${overdueFees.length} overdue fee(s) across the association`,
      action: 'Review',
    });
  }

  // Events — REAL upcoming meetings.
  const events: EventItem[] = upcomingMeetings.slice(0, 3).map(m => {
    const d = new Date(m.date);
    return {
      t: d.toLocaleString('en-CA', { month: 'short' }),
      ap: String(d.getDate()),
      tone: 'violet',
      h: m.title,
      s: `${m.startTime ?? ''} · ${m.location}`,
    };
  });

  const boardName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="wpage wide fadein">
      <SFGreeting
        hi={`Welcome, ${boardName}`}
        badge="BOARD"
        sub={<span>{currentCondo?.name ?? 'No association selected'} · {unitStats.totalUnits} units</span>}
        chips={chips}
        btns={
          <>
            <button className="hb ghost" type="button">
              <FileTextOutlined /> Financials
            </button>
            <button className="hb solid" type="button">
              <CalendarOutlined /> Board meeting
            </button>
          </>
        }
      />

      {summaryError ? (
        <div className="home-error" role="alert">
          We couldn't load some board data ({summaryError}).{' '}
          <Button size="small" onClick={retrySummary} style={{ marginLeft: 8 }}>Retry</Button>
        </div>
      ) : error ? (
        <div className="home-error" role="alert">
          We couldn't load some board data ({error}). The figures below may be
          incomplete — please retry shortly.
        </div>
      ) : null}

      <div className="sfgrid">
        <div className="sfcol">
          {/* Awaiting board approval — REAL open resolutions, honest empty state */}
          <section className="c p" aria-labelledby="b-approval-h">
            <div className="ch">
              <h2 id="b-approval-h">Awaiting board approval</h2>
              <span className={'bd ' + (openResolutions.length ? 'amber' : 'green')}>
                <span className="pd" />{openResolutions.length} item{openResolutions.length === 1 ? '' : 's'}
              </span>
            </div>
            {openResolutions.length === 0 && (
              <div className="rempty" style={{ padding: '12px 0' }}>
                Nothing is awaiting your approval right now.
              </div>
            )}
            {openResolutions.slice(0, 5).map(r => (
              <div className="rec-row" key={r.id}>
                <span className="ricon" style={{ background: 'var(--blue)' }}>
                  <CheckOutlined />
                </span>
                <div className="rmain">
                  <div className="h">{r.title}</div>
                  <div className="s">{r.category} · #{r.voteNumber}</div>
                </div>
                <span className="lk" style={{ alignSelf: 'center' }}>Review</span>
              </div>
            ))}
          </section>

          {/* Performance — REAL collected-revenue series (NOT a faked budget line) */}
          <section className="c p perf" aria-labelledby="b-revenue-h">
            <div className="pf-top">
              <div>
                <div className="ch" style={{ marginBottom: 0 }}><h2 id="b-revenue-h">Operating revenue collected · last 6 months</h2></div>
                <div className="pf-big num">{currency(totalCollected)}</div>
                <div className="pf-goal">
                  {hasChartData ? (
                    <>Actual collections shown · <b>budget-vs-actual</b> arrives when budget data is wired</>
                  ) : (
                    <span className="home-note">No payment history yet for this association</span>
                  )}
                </div>
              </div>
            </div>
            <AtriumAreaChart
              points={chartPoints}
              labels={chartLabels}
              color="#2563EB"
              valueFormatter={currency}
              ariaLabel={
                hasChartData
                  ? `Operating revenue collected over the last 6 months: ${chartLabels
                      .map((m, i) => `${m} ${currency(chartPoints[i])}`)
                      .join(', ')}.`
                  : 'Operating revenue collected over the last 6 months: no payment history yet for this association.'
              }
            />
          </section>

          {/* Stat row — REAL. Gated on first load (106.2): skeleton rather than
              a confident $0 delinquency / 0% occupancy before data lands. */}
          {summaryLoading && !summaryError ? (
            <div className="c p"><Skeleton active paragraph={{ rows: 3 }} /></div>
          ) : (
          <section className="statrow" aria-label="Association summary" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat">
              <div className="gi"><TeamOutlined /></div>
              <div className="v">{unitStats.occupancyRate}%</div>
              <div className="l">Occupancy</div>
              <div className="d nu">{unitStats.occupiedUnits}/{unitStats.totalUnits}</div>
            </div>
            <div className="stat">
              <div className="gi"><DollarOutlined /></div>
              <div className="v num">{currency(delinquencyTotal)}</div>
              <div className="l">Delinquency</div>
              <div className="d nu">{overdueFees.length} fee(s)</div>
            </div>
            <div className="stat">
              <div className="gi"><ToolOutlined /></div>
              <div className="v">{openWorkOrders.length}</div>
              <div className="l">Open work orders</div>
            </div>
            <div className="stat">
              <div className="gi"><WarningOutlined /></div>
              <div className="v">{openResolutions.length}</div>
              <div className="l">Open votes</div>
            </div>
          </section>
          )}
        </div>

        {/* Right rail */}
        <div className="sfcol">
          <Assistant items={assistantItems} />
          <EventsCard title="Upcoming meetings" items={events} />

          {/* Documents to review — REAL board/financial docs */}
          <RailCard icon={<FileTextOutlined />} tone="var(--teal)" title="Documents to review" count={reviewDocs.length}>
            {reviewDocs.length === 0 && <div className="rempty">No documents queued for review.</div>}
            {reviewDocs.map(d => (
              <div className="rec-row" key={d.id}>
                <span className="ricon" style={{ background: 'var(--teal)' }}><FileTextOutlined /></span>
                <div className="rmain">
                  <div className="h">{d.title}</div>
                  <div className="s">{d.category} · {formatDate(d.uploadDate)}</div>
                </div>
                <span className="lk" style={{ alignSelf: 'center' }}>Open</span>
              </div>
            ))}
          </RailCard>
        </div>
      </div>
    </div>
  );
};

export default AtriumBoardHome;
